import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

import {
  createDefaultScoringSection,
  resolveScoringItemId
} from './scoringShared'

function getSectionCount(items = []) {
  return (Array.isArray(items) ? items : []).reduce((count, item) => {
    if (item?.type !== ITEM_TYPES.SEPARATOR) {
      return count
    }

    return count + 1
  }, 0)
}

function findDefaultSection(items = []) {
  return (Array.isArray(items) ? items : []).find(
    item => item?.isDefaultSection
  )
}

function hasOrphanQuestions(items = []) {
  let foundSection = false

  return (Array.isArray(items) ? items : []).some(item => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      foundSection = true
      return false
    }

    return !foundSection
  })
}

function prependDefaultSectionForOrphans(items = [], sectionName = '') {
  const checklistItems = Array.isArray(items) ? [...items] : []
  if (_.isEmpty(checklistItems)) {
    return checklistItems
  }

  if (!hasOrphanQuestions(checklistItems)) {
    return checklistItems
  }

  const defaultSection = findDefaultSection(checklistItems)
  if (defaultSection) {
    return checklistItems
  }

  const weightPercentage = getSectionCount(checklistItems) === 0 ? 100 : null

  return [
    createDefaultScoringSection({
      name: sectionName,
      weightPercentage,
    }),
    ...checklistItems,
  ]
}

function moveOrphansIntoDefaultSection(items = []) {
  const checklistItems = Array.isArray(items) ? [...items] : []
  const defaultSection = findDefaultSection(checklistItems)

  if (!defaultSection || !hasOrphanQuestions(checklistItems)) {
    return checklistItems
  }

  const orphanQuestions = []
  const normalizedItems = []
  let foundSection = false

  checklistItems.forEach(item => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      foundSection = true
      normalizedItems.push(item)
      return
    }

    if (!foundSection) {
      orphanQuestions.push(item)
      return
    }

    normalizedItems.push(item)
  })

  if (_.isEmpty(orphanQuestions)) {
    return checklistItems
  }

  const defaultSectionIndex = normalizedItems.findIndex(
    item => item?.isDefaultSection
  )

  if (defaultSectionIndex === -1) {
    return checklistItems
  }

  return [
    ...normalizedItems.slice(0, defaultSectionIndex + 1),
    ...orphanQuestions,
    ...normalizedItems.slice(defaultSectionIndex + 1),
  ]
}

function moveOrphanQuestionsToSection(items = [], targetSectionId = null) {
  const checklistItems = Array.isArray(items) ? [...items] : []

  if (!targetSectionId || !hasOrphanQuestions(checklistItems)) {
    return checklistItems
  }

  const targetSection = checklistItems.find(
    (item, index) =>
      item?.type === ITEM_TYPES.SEPARATOR &&
      resolveScoringItemId(item, `scoring-item-${index}`) === targetSectionId
  )

  if (!targetSection) {
    return checklistItems
  }

  const orphanQuestions = []
  const normalizedItems = []
  let foundSection = false

  checklistItems.forEach(item => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      foundSection = true
      normalizedItems.push(item)
      return
    }

    if (!foundSection) {
      orphanQuestions.push(item)
      return
    }

    normalizedItems.push(item)
  })

  if (_.isEmpty(orphanQuestions)) {
    return checklistItems
  }

  const targetSectionIndex = normalizedItems.indexOf(targetSection)

  if (targetSectionIndex === -1) {
    return checklistItems
  }

  return [
    ...normalizedItems.slice(0, targetSectionIndex + 1),
    ...orphanQuestions,
    ...normalizedItems.slice(targetSectionIndex + 1),
  ]
}

function getOrphanQuestionRecoveryMode(
  items = [],
  { isConditionalLogicEnabled = false } = {}
) {
  const checklistItems = Array.isArray(items) ? items : []

  if (!hasOrphanQuestions(checklistItems)) {
    return 'none'
  }

  // Flag-on only: conditioned sections never host relocated orphans, so a
  // checklist whose every section is conditioned has no move target and the
  // flow collapses to create-only.
  const hasMoveTarget = checklistItems.some(item => {
    if (item?.type !== ITEM_TYPES.SEPARATOR) {
      return false
    }

    if (isConditionalLogicEnabled && utils.checklists.hasVisibilityRule(item)) {
      return false
    }

    return true
  })

  return hasMoveTarget ? 'decide' : 'create'
}

function getOrphanQuestionRecoverySections({
  items = [],
  getFallbackSectionLabel = ({ number }) => `Section ${number}`,
  isConditionalLogicEnabled = false,
} = {}) {
  const checklistItems = Array.isArray(items) ? items : []
  const sectionCount = getSectionCount(checklistItems)
  let currentSectionNumber = 0

  return checklistItems.reduce((sections, item, index) => {
    if (item?.type !== ITEM_TYPES.SEPARATOR) {
      return sections
    }

    // Flag-on only: conditioned sections never host relocated orphans.
    if (isConditionalLogicEnabled && utils.checklists.hasVisibilityRule(item)) {
      return sections
    }

    currentSectionNumber += 1

    sections.push({
      text:
        item?.name ||
        getFallbackSectionLabel({
          number: sectionCount > 1 ? currentSectionNumber : 1,
        }),
      value: resolveScoringItemId(item, `scoring-item-${index}`),
    })

    return sections
  }, [])
}

function getSectionBoundary(items = [], separatorIndex = -1) {
  const checklistItems = Array.isArray(items) ? items : []
  const separator = checklistItems[separatorIndex]

  if (separator?.type !== ITEM_TYPES.SEPARATOR) {
    return null
  }

  let endIndex = checklistItems.length

  for (let i = separatorIndex + 1; i < checklistItems.length; i++) {
    if (checklistItems[i]?.type === ITEM_TYPES.SEPARATOR) {
      endIndex = i
      break
    }
  }

  return {
    startIndex: separatorIndex,
    endIndex,
  }
}

function removeSectionWithQuestions(items = [], separatorIndex = -1) {
  const checklistItems = Array.isArray(items) ? [...items] : []
  const boundary = getSectionBoundary(checklistItems, separatorIndex)

  if (!boundary) {
    return checklistItems
  }

  return [
    ...checklistItems.slice(0, boundary.startIndex),
    ...checklistItems.slice(boundary.endIndex),
  ]
}

function getSectionQuestions(items = [], separatorIndex = -1) {
  const checklistItems = Array.isArray(items) ? items : []
  const boundary = getSectionBoundary(checklistItems, separatorIndex)

  if (!boundary) {
    return []
  }

  return checklistItems.slice(boundary.startIndex + 1, boundary.endIndex)
}

function moveSectionQuestionsToSection(
  items = [],
  separatorIndex = -1,
  targetSectionId = null
) {
  const checklistItems = Array.isArray(items) ? [...items] : []
  const boundary = getSectionBoundary(checklistItems, separatorIndex)

  if (!boundary || !targetSectionId) {
    return checklistItems
  }

  const targetSection = checklistItems.find(
    (item, index) =>
      index !== separatorIndex &&
      item?.type === ITEM_TYPES.SEPARATOR &&
      resolveScoringItemId(item, `scoring-item-${index}`) === targetSectionId
  )

  if (!targetSection) {
    return checklistItems
  }

  const sectionQuestions = getSectionQuestions(checklistItems, separatorIndex)

  if (_.isEmpty(sectionQuestions)) {
    return removeSectionWithQuestions(checklistItems, separatorIndex)
  }

  const itemsWithoutSection = removeSectionWithQuestions(
    checklistItems,
    separatorIndex
  )
  const targetSectionIndex = itemsWithoutSection.indexOf(targetSection)

  if (targetSectionIndex === -1) {
    return checklistItems
  }

  return [
    ...itemsWithoutSection.slice(0, targetSectionIndex + 1),
    ...sectionQuestions,
    ...itemsWithoutSection.slice(targetSectionIndex + 1),
  ]
}

export {
  findDefaultSection,
  getOrphanQuestionRecoveryMode,
  getOrphanQuestionRecoverySections,
  getSectionCount,
  getSectionQuestions,
  hasOrphanQuestions,
  moveOrphanQuestionsToSection,
  moveSectionQuestionsToSection,
  moveOrphansIntoDefaultSection,
  removeSectionWithQuestions,
  prependDefaultSectionForOrphans
}
